import { FC } from "react";
import {
  Grid,
  Card,
  CardActionArea,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { KaerIcon } from "@/icons/kaerIcon";

interface Props {
  name?: string;
  id?: string;
  onClickHandler: (id: string, name: string) => void;
  folderId?: string;
  fileCount?: number;
  query?: string;
  twoColumnLayout?: boolean;
}

const SimpleFolderCard: FC<Props> = ({
  name = "",
  id = "",
  onClickHandler,
  folderId,
  fileCount = 0,
  query,
  twoColumnLayout = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isActive = id === folderId;

  const capitalizeFirstLetter = (value: string) =>
    value.charAt(0).toUpperCase() + value.slice(1);

  const renderIcon = () => {
    switch (name) {
      case "upload":
        return <KaerIcon icon="upload" color="#8c1ec8" />;
      case "Berichte von kaer":
        return <KaerIcon icon="clipboard" color="#ff6428" />;
      default:
        return <KaerIcon icon="folder" color="#008c7d" />;
    }
  };

  const folderTitle =
    name === "Berichte von kaer"
      ? t("reports_from_kaer")
      : capitalizeFirstLetter(name);

  const gridSize = twoColumnLayout
    ? { xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }
    : { xs: 12, sm: 6, md: 4, lg: 3, xl: 3 };

  return (
    <Grid item {...gridSize}>
      <Card
        elevation={0}
        sx={{
          borderRadius: "0.75rem !important",
          backgroundColor: query && "#f4f2f2",
          border:
            isActive && !query ? `2px solid #8c1ec8` : "1px solid #ECECEC",
          transition: "all 0.15s ease",
          "&:hover": {
            boxShadow: isMobile ? "none" : "0 4px 16px rgba(0,0,0,0.06)",
          },
        }}
      >
        <CardActionArea
          data-testid="simple-folder-card"
          data-folder-id={id}
          title={folderTitle}
          onClick={() => onClickHandler(id, name)}
          sx={{
            padding: isMobile ? 0.5 : 1.5,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              flex={1}
              minWidth={0}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={isMobile ? 30 : 36}
                height={isMobile ? 30 : 36}
                borderRadius={"0.75rem"}
                sx={{
                  backgroundColor: "#f4f2f2",
                  flexShrink: 0,
                }}
              >
                {renderIcon()}
              </Box>

              <Typography
                variant={isMobile ? "body2" : "body1"}
                fontWeight={isActive ? 700 : 600}
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {folderTitle}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              flexShrink={0}
              sx={{
                minWidth: 45,
                textAlign: "right",
              }}
            >
              {t("fileitemcount", { count: fileCount })}
            </Typography>
          </Box>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

export default SimpleFolderCard;
